import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCountry } from '../../hooks/useCountry';
import { getLanguageInfo } from '../../config/countries';

const SubdomainLayout = () => {
  const { countryCode, country: countryConfig, theme } = useCountry();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Hide header/footer on wizard and preview pages
  const hideChrome = /^\/(contracts\/(create|[^/]+\/(preview|download)))/.test(location.pathname);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Set language from country config on mount
  useEffect(() => {
    if (countryConfig?.defaultLanguage && i18n.language !== countryConfig.defaultLanguage) {
      i18n.changeLanguage(countryConfig.defaultLanguage);
    }
  }, [countryConfig, i18n]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLanguageChange = (newLang) => {
    i18n.changeLanguage(newLang);
    setMobileMenuOpen(false);
  };

  const languages = countryConfig?.languages || [];
  const colors = theme?.colors || {};

  // Show only the first part of lawRef (before "·") for the nav badge
  const lawBadge = theme?.local?.lawRef
    ? theme.local.lawRef.split('·')[0].trim()
    : theme?.legalBadge || null;

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Themed Background */}
      <div className="fixed inset-0 -z-10" style={{ background: theme ? `linear-gradient(180deg, ${colors.primary}0A 0%, white 20%, white 80%, ${colors.primary}05 100%)` : undefined }}>
        {!theme && <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100" />}
      </div>

      {/* Header */}
      {!hideChrome && (
        <header
          className="sticky top-0 z-50 border-b backdrop-blur-xl"
          style={{
            backgroundColor: theme ? `${colors.primary}F0` : 'rgba(255,255,255,0.92)',
            borderColor: theme ? `${colors.primary}30` : 'rgba(0,0,0,0.06)',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">

              {/* Logo — no flag, just brand + subtle law badge */}
              <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0">
                <div
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shadow-md flex-shrink-0"
                  style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="flex flex-col leading-none">
                  <span className={`text-base sm:text-lg font-bold tracking-tight ${theme ? 'text-white' : 'text-gray-900'}`}>
                    {t('common.appName')}
                  </span>
                  {lawBadge && (
                    <span className={`text-[10px] sm:text-xs font-medium hidden sm:block ${theme ? 'text-white/60' : 'text-gray-400'}`}>
                      {lawBadge}
                    </span>
                  )}
                </div>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                <Link
                  to="/"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${theme ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  {t('common.home', 'Home')}
                </Link>
                <Link
                  to="/about"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${theme ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  {t('footer.links.about', 'About')}
                </Link>
                <Link
                  to="/contact"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${theme ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  {t('footer.links.contact', 'Contact')}
                </Link>

                {/* Language Switcher */}
                {languages.length > 1 && (
                  <div className={`flex items-center gap-0.5 p-1 rounded-lg ml-1 ${theme ? 'bg-white/10' : 'bg-gray-100'}`}>
                    {languages.map((langCode) => {
                      const langInfo = getLanguageInfo(langCode);
                      const active = i18n.language === langCode;
                      return (
                        <button
                          key={langCode}
                          onClick={() => handleLanguageChange(langCode)}
                          className="px-2.5 py-1 rounded-md text-sm font-medium transition-all"
                          style={active
                            ? { background: theme ? 'rgba(255,255,255,0.25)' : (colors.buttonBg || '#6366f1'), color: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }
                            : { color: theme ? 'rgba(255,255,255,0.65)' : '#6B7280' }
                          }
                        >
                          {langInfo?.nativeName || langCode}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* CTA */}
                <Link
                  to="/contracts/create"
                  className="ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:-translate-y-px hover:shadow-lg"
                  style={{ background: theme ? 'rgba(255,255,255,0.2)' : (colors.buttonBg || 'linear-gradient(135deg, #6366f1, #8b5cf6)'), backdropFilter: 'blur(4px)', border: theme ? '1px solid rgba(255,255,255,0.3)' : 'none' }}
                >
                  {t('home.hero.cta', 'Create Contract')}
                </Link>
              </nav>

              {/* Mobile right side: CTA + hamburger */}
              <div className="flex items-center gap-2 md:hidden">
                <Link
                  to="/contracts/create"
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                  style={{ background: theme ? 'rgba(255,255,255,0.2)' : (colors.buttonBg || 'linear-gradient(135deg, #6366f1, #8b5cf6)'), border: theme ? '1px solid rgba(255,255,255,0.3)' : 'none' }}
                >
                  {t('home.hero.cta', 'Create')}
                </Link>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className={`p-2 rounded-lg transition-colors ${theme ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                  aria-label="Menu"
                >
                  {mobileMenuOpen ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Mobile Menu — full-screen overlay panel */}
      {mobileMenuOpen && !hideChrome && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Panel slides in from right */}
          <div
            className="fixed top-0 right-0 bottom-0 z-50 w-72 md:hidden flex flex-col shadow-2xl"
            style={{ backgroundColor: theme ? colors.primary : 'white' }}
          >
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: theme ? 'rgba(255,255,255,0.15)' : '#f3f4f6' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: theme ? 'rgba(255,255,255,0.2)' : (colors.buttonBg || '#6366f1') }}>
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className={`font-bold text-base ${theme ? 'text-white' : 'text-gray-900'}`}>{t('common.appName')}</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className={`p-2 rounded-lg ${theme ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Law badge in menu */}
            {lawBadge && (
              <div className="px-5 py-3" style={{ borderBottom: theme ? '1px solid rgba(255,255,255,0.1)' : '1px solid #f3f4f6' }}>
                <span className={`text-xs font-medium ${theme ? 'text-white/50' : 'text-gray-400'}`}>{lawBadge}</span>
              </div>
            )}

            {/* Navigation links */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {[
                { to: '/', label: t('common.home', 'Home') },
                { to: '/about', label: t('footer.links.about', 'About') },
                { to: '/contact', label: t('footer.links.contact', 'Contact') },
              ].map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-colors ${theme ? 'text-white/85 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`}
                >
                  {label}
                </Link>
              ))}

              {/* Language switcher */}
              {languages.length > 1 && (
                <div className="pt-3 pb-1">
                  <p className={`text-xs font-semibold uppercase tracking-wider px-4 mb-2 ${theme ? 'text-white/40' : 'text-gray-400'}`}>
                    {t('common.language', 'Language')}
                  </p>
                  <div className="flex flex-wrap gap-2 px-4">
                    {languages.map((langCode) => {
                      const langInfo = getLanguageInfo(langCode);
                      const active = i18n.language === langCode;
                      return (
                        <button
                          key={langCode}
                          onClick={() => handleLanguageChange(langCode)}
                          className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                          style={active
                            ? { background: theme ? 'rgba(255,255,255,0.25)' : (colors.buttonBg || '#6366f1'), color: 'white' }
                            : { backgroundColor: theme ? 'rgba(255,255,255,0.08)' : '#f3f4f6', color: theme ? 'rgba(255,255,255,0.75)' : '#374151' }
                          }
                        >
                          {langInfo?.nativeName || langCode}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </nav>

            {/* Sticky CTA at bottom of panel */}
            <div className="px-4 py-5 border-t" style={{ borderColor: theme ? 'rgba(255,255,255,0.12)' : '#f3f4f6' }}>
              <Link
                to="/contracts/create"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl text-sm font-bold transition-all active:scale-95"
                style={theme
                  ? { background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }
                  : { background: colors.buttonBg || 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' }
                }
              >
                {t('home.hero.cta', 'Create Contract')}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      {!hideChrome && (
        <footer className="text-white py-10 sm:py-14" style={{ backgroundColor: colors.footerBg || '#111827' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-8">
              {/* Brand column — full width on mobile */}
              <div className="col-span-2 md:col-span-1">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: colors.buttonBg || 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <span className="text-lg font-bold">{t('common.appName')}</span>
                </div>
                {/* Local law line in footer */}
                {theme?.local?.lawRef && (
                  <p className="text-gray-400 text-xs mb-2">{theme.local.lawRef}</p>
                )}
                <p className="text-gray-400 text-sm leading-relaxed">{t('footer.description')}</p>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold text-sm sm:text-base mb-4">{t('footer.quickLinks', 'Quick Links')}</h4>
                <div className="space-y-2.5">
                  <Link to="/about" className="block text-gray-400 hover:text-white transition-colors text-sm">{t('footer.links.about')}</Link>
                  <Link to="/privacy" className="block text-gray-400 hover:text-white transition-colors text-sm">{t('footer.links.privacy')}</Link>
                  <Link to="/terms" className="block text-gray-400 hover:text-white transition-colors text-sm">{t('footer.links.terms')}</Link>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-semibold text-sm sm:text-base mb-4">{t('footer.links.contact')}</h4>
                <p className="text-gray-400 text-sm">support@rentydoc.com</p>
                {theme?.local?.disputeBody && (
                  <p className="text-gray-500 text-xs mt-3 leading-relaxed">{theme.local.disputeBody}</p>
                )}
              </div>
            </div>

            <div className="pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-gray-500 text-xs sm:text-sm">{t('footer.copyright')}</p>
              {theme?.local?.currencyNote && (
                <p className="text-gray-600 text-xs">{theme.local.currencyNote}</p>
              )}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default SubdomainLayout;
